import { useQuery } from '@tanstack/react-query';
import { useSupabaseClient } from './use-supabase';

/**
 * Reads the per-caller slice of `user_frequent_pages` via the
 * `get_my_frequent_pages(p_limit int)` SECURITY DEFINER RPC.
 *
 * WHY NOT DIRECT VIEW ACCESS:
 *   Materialized views do not support RLS in Postgres. Granting
 *   SELECT to `authenticated` would expose every user's visit
 *   history to every other user. The IA1 foundation migration
 *   therefore revokes direct access and exposes the data only via
 *   the RPC, which filters on `auth.uid() = user_id` and
 *   `org_id = current_org()` server-side.
 *
 *   Client-side filtering (which a previous draft of this hook
 *   used) is not a security boundary — any authenticated Supabase
 *   client could otherwise call `.from('user_frequent_pages').select('*')`
 *   and read the entire table.
 *
 * View definition (informational): rolling 30 days, ≥3 visits per
 * (user, org, route). Refreshed hourly at :17 by pg_cron.
 */

export interface FrequentPageRow {
  route: string;
  module_slug: string | null;
  workspace_slug: string | null;
  page_title: string | null;
  visit_count: number;
  last_visited_at: string;
}

interface RpcRow {
  route: string;
  module_slug: string | null;
  workspace_slug: string | null;
  page_title: string | null;
  visit_count: number;
  last_visited_at: string;
}

export function useFrequentPages(orgId: string | null) {
  const supabase = useSupabaseClient();

  // User identity in the cache key so signing out + signing in (or
  // switching users in a shared browser tab) doesn't serve user A's
  // cached frequent-page history to user B until staleTime elapses.
  // Same defence the org dimension already provides for tenant switches.
  const { data: userId = null } = useQuery({
    queryKey: ['user_frequent_pages', 'current-user-id'],
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return data.user?.id ?? null;
    },
  });

  return useQuery({
    queryKey: ['user_frequent_pages', userId, orgId],
    enabled: !!orgId && !!userId,
    // View refreshes hourly — cache aggressively. Manual invalidate
    // not needed; the page is non-interactive.
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<FrequentPageRow[]> => {
      if (!userId || !orgId) return [];

      const { data, error } = await supabase.rpc('get_my_frequent_pages', { p_limit: 200 });
      if (error) throw error;
      // RPC is scoped server-side by auth.uid() + current_org(); the
      // returned rows are guaranteed to belong to the caller for the
      // current org. We do NOT stamp client-side orgId/userId onto
      // rows — if the JWT claim lagged behind the client-selected
      // org, that synthesis would mislabel rows. Server is the
      // source of truth.
      return (data ?? []) as FrequentPageRow[];
    },
  });
}
